import type BABYLON from 'babylonjs';

import { createArViewerRunner } from './arViewer';
import { attachCameraInput, focusMeshes, isUserMesh } from './camera';
import { setCursor } from './cursor';
import type { EngineCanvasOrContext, EngineOptions } from './engine';
import { createEngine } from './engine';
import type { FilesInputOptions } from './filesInput';
import { createFilesInput } from './filesInput';
import type { Globals } from './globals';
import { loadGlobals } from './globals';
import type { LoggerOptions } from './logger';
import { loggerError, updateLogger } from './logger';
import type { ImportMeshAsyncOptions } from './mesh';
import { importMeshAsync } from './mesh';
import type { MeshRenderingOptions } from './meshRendering';
import { updateMeshesRendering } from './meshRendering';
import { mergeOptions } from './options';
import { addOnceObserverOnNewMeshAdded } from './scene';
import type { SceneAndEnviromentOptions } from './sceneAndEnvironment';
import { createOrUpdateSceneAndEnviroment } from './sceneAndEnvironment';

export interface VViewerUpdateOptions extends SceneAndEnviromentOptions {
  meshRendering?: MeshRenderingOptions;
  filesInput?: FilesInputOptions | null;
}

export interface VViewerOptions extends VViewerUpdateOptions {
  engine?: EngineOptions;
  onError?: LoggerOptions['onError'];
}

export interface VViewerImportMeshAsync extends ImportMeshAsyncOptions {
  usdzUrl?: string;
  autoFocus?: boolean;
}

export class VViewer {
  static async create(
    canvasOrContext: ConstructorParameters<typeof VViewer>[0],
    options?: ConstructorParameters<typeof VViewer>[1],
    globalUrls?: Parameters<typeof loadGlobals>[0]
  ): Promise<VViewer> {
    const globals = await loadGlobals(globalUrls);
    return new VViewer(canvasOrContext, options, globals);
  }

  globals: Globals;

  containers: BABYLON.AssetContainer[];

  onMeshLoadedObservable: BABYLON.Observable<BABYLON.AssetContainer>;

  meshRenderingOptions?: MeshRenderingOptions;

  engine: BABYLON.Engine;

  scene: BABYLON.Scene;

  environmentHelper: BABYLON.Nullable<BABYLON.EnvironmentHelper>;

  filesInput?: BABYLON.Nullable<BABYLON.FilesInput>;

  arView?: ReturnType<typeof createArViewerRunner>;

  sceneAndEnviromentOptions?: SceneAndEnviromentOptions;

  onArViewChanged?: (this: VViewer) => void;

  onSceneGrabbing?: () => void;

  constructor(
    canvasOrContext: EngineCanvasOrContext,
    {
      engine: engineOptions,
      meshRendering: meshRenderingOptions,
      filesInput: filesInputOptions,
      onError,
      ...options
    }: VViewerOptions = {},
    globals: Globals = globalThis as unknown as Globals
  ) {
    this.globals = globals;
    this.containers = [];
    this.onMeshLoadedObservable = new this.globals.BABYLON.Observable();
    this.meshRenderingOptions = meshRenderingOptions;
    updateLogger(this.globals, { onError });

    this.engine = createEngine(this.globals, canvasOrContext, engineOptions);
    [this.scene, this.environmentHelper, this.sceneAndEnviromentOptions] =
      createOrUpdateSceneAndEnviroment(this.globals, this.engine, null, options);
    this.setupCamera();
    addOnceObserverOnNewMeshAdded(this.scene, () => {
      this.engine.runRenderLoop(() => {
        this.scene.render();
      });
    });
    if (filesInputOptions) {
      this.setupFilesInput(filesInputOptions);
    }

    this.onMeshLoadedObservable.add((container) => {
      updateMeshesRendering(this.globals, container.meshes, this.meshRenderingOptions);
      this.environmentHelper?.groundMirrorRenderList?.push(...container.meshes);
      this.containers.push(container);
    });
  }

  private setupCamera(): void {
    const camera = this.scene.activeCamera;
    if (camera instanceof this.globals.BABYLON.ArcRotateCamera) {
      setCursor(this.scene, 'grab');
      attachCameraInput(camera, {
        onButtonDown: () => {
          setCursor(this.scene, 'grabbing');
          this.onSceneGrabbing?.();
        },
        onButtonUp() {
          setCursor(this.camera.getScene(), 'grab');
        },
        onLostFocus() {
          setCursor(this.camera.getScene(), 'grab');
        },
      });
    }
  }

  private setupFilesInput(filesInputOptions: FilesInputOptions | null): void {
    this.filesInput?.dispose();
    // cf. https://github.com/BabylonJS/Babylon.js/blob/5.48.1/packages/tools/sandbox/src/components/renderingZone.tsx#L96
    this.filesInput = filesInputOptions
      ? createFilesInput(this.globals, this.engine, {
          ...filesInputOptions,
          onProcessFileCallback: (file, name, extension, _setSceneFileToLoad) => {
            switch (extension.toLowerCase()) {
              case 'hdr':
              case 'env':
                this.globals.BABYLON.FilesInput.FilesToLoad[name] = file;
                this.update({
                  scene: {
                    environmentTexture: `file:${
                      (file as File & { correctName: string }).correctName
                    }`,
                  },
                });
                return false;
              default:
                return true;
            }
          },
          loadAsync: async (sceneFile, onProgress) => {
            if (sceneFile.size) {
              this.disposeContainers();
              this.disposeSceneAndEnviroment();
              [this.scene, this.environmentHelper, this.sceneAndEnviromentOptions] =
                createOrUpdateSceneAndEnviroment(
                  this.globals,
                  this.engine,
                  null,
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  this.sceneAndEnviromentOptions!
                );
              this.setupCamera();
              await this.scene.whenReadyAsync(); // Autofocus is broken without it
              await this.importMeshAsync(sceneFile, { rootUrl: 'file:', onProgress });
            } else if (sceneFile.name) {
              const err = new Error(`${sceneFile.name} is empty or cannot be accessed`);
              loggerError(this.globals, err);
              throw err;
            }
            return this.scene;
          },
        })
      : null;
    // Set a special empty file when filesInput is not used to implement hdr only replacement.
    if (this.filesInput) {
      // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/no-explicit-any
      (this.filesInput as any)._sceneFileToLoad = new File([], '');
    }
  }

  disposeContainers(): void {
    this.containers.forEach((container) => {
      container.dispose();
    });
    this.containers.splice(0, this.containers.length);
    this.arView = undefined;
    this.onArViewChanged?.call(this);
  }

  disposeSceneAndEnviroment(): void {
    this.environmentHelper?.dispose();
    this.environmentHelper = null;
    this.engine.activeRenderLoops.forEach((renderLoop) => {
      this.engine.stopRenderLoop(renderLoop);
    });
    this.engine.scenes.forEach((scene) => {
      scene.cameras.forEach((camera) => {
        camera.detachControl();
        camera.dispose();
      });
      scene.dispose();
    });
  }

  dispose(): void {
    this.disposeContainers();
    this.disposeSceneAndEnviroment();
    this.setupFilesInput(null);
    this.engine.dispose();
    this.sceneAndEnviromentOptions = undefined;
    this.onArViewChanged = undefined;
  }

  update({
    meshRendering: meshRenderingOptions,
    filesInput: filesInputOptions,
    ...options
  }: VViewerUpdateOptions): void {
    [this.scene, this.environmentHelper, this.sceneAndEnviromentOptions] =
      createOrUpdateSceneAndEnviroment(
        this.globals,
        this.engine,
        this.environmentHelper,
        options,
        this.sceneAndEnviromentOptions
      );
    if (meshRenderingOptions) {
      const [nextOptions, diffOptions] = mergeOptions(
        meshRenderingOptions,
        this.meshRenderingOptions
      );
      if (nextOptions && diffOptions) {
        this.meshRenderingOptions = nextOptions;
        this.containers.forEach((container) => {
          updateMeshesRendering(this.globals, container.meshes, nextOptions, !!diffOptions.channel);
        });
      }
    }
    if (filesInputOptions !== undefined) {
      this.setupFilesInput(filesInputOptions);
    }
  }

  async importMeshAsync(
    urlOrFile: string | File,
    { autoFocus: rawAutoFocus, usdzUrl, ...options }: VViewerImportMeshAsync = {}
  ): Promise<BABYLON.AssetContainer> {
    const autoFocus =
      rawAutoFocus != null
        ? rawAutoFocus
        : this.scene.meshes.filter((mesh) => isUserMesh(this.globals, mesh)).length === 0;
    const container = new this.globals.BABYLON.AssetContainer();
    container.scene = this.scene;
    await importMeshAsync(this.globals, container, urlOrFile, options);
    await this.onMeshLoadedObservable.notifyObserversWithPromise(container);
    if (this.arView === undefined) {
      this.arView = createArViewerRunner({ glb: urlOrFile, usdz: usdzUrl });
      this.onArViewChanged?.call(this);
    }
    if (autoFocus) focusMeshes(this.globals, this.scene, container.meshes);
    return container;
  }
}
